import { useNavigate, useLocation } from "react-router-dom";
import { Compass, Phone, Mail, MapPin, Globe } from "lucide-react";
import { PHONE, PHONE_LINK, EMAIL, ADDRESS, SITE_DISCLAIMER } from "../data/content";
import { scrollToSection } from "../lib/motion";
import { t } from "../i18n";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (to) => (e) => {
    e.preventDefault();
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
      else scrollToSection(id);
    } else navigate(to);
  };

  const cols = [
    {
      title: t("footer.company"),
      links: [
        { label: t("nav.about"), to: "/about" },
        { label: t("nav.why"), to: "/#why-choose" },
        { label: t("nav.contact"), to: "/contact" },
      ],
    },
    {
      title: t("footer.servicesCol"),
      links: [
        { label: "Work Permit Visa", to: "/work-permit-visa" },
        { label: "Visitor Visa", to: "/visitor-visa" },
        { label: "Student Visa", to: "/student-visa" },
        { label: "Family / Spouse Visa", to: "/family-spouse-visa" },
        { label: "Business Visa", to: "/business-visa" },
        { label: "Documentation Assistance", to: "/documentation-assistance" },
      ],
    },
    {
      title: t("footer.quick"),
      links: [
        { label: t("nav.countries"), to: "/countries" },
        { label: t("footer.eligibility"), to: "/eligibility" },
        { label: t("nav.faq"), to: "/#faq" },
        { label: t("nav.blog"), to: "/blog" },
        { label: t("nav.contact"), to: "/contact" },
      ],
    },
  ];

  return (
    <footer data-testid="footer-section" className="relative bg-navy-deep border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                <Compass className="h-5 w-5 text-gold" />
              </span>
              <span className="font-display font-extrabold tracking-tight text-2xl text-white">
                THE TRIP <span className="text-gold">WORLD</span>
              </span>
            </div>
            <p className="font-display text-sm tracking-[0.3em] text-gold/80 mb-5">YOUR JOURNEY STARTS HERE</p>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              {t("footer.blurb")}
            </p>
          </div>
          {cols.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="font-display text-xs font-bold tracking-[0.25em] text-gold mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.to}
                      onClick={go(l.to)}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-slate-400 hover:text-gold transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-2">
            <h4 className="font-display text-xs font-bold tracking-[0.25em] text-gold mb-5">{t("footer.contactCol")}</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="font-semibold text-slate-200">Mr. Dheeraj Kataria</li>
              <li>
                <a href={PHONE_LINK} className="flex items-start gap-2 hover:text-gold transition-colors" data-testid="footer-phone">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-gold" /> {PHONE}
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} className="flex items-start gap-2 hover:text-gold transition-colors break-all" data-testid="footer-email">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-gold" /> {EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Globe className="h-4 w-4 mt-0.5 shrink-0 text-gold" /> thetrip.world
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold" /> Ahmadgarh, Punjab, India
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5">
          <p className="text-xs text-slate-500 leading-relaxed max-w-4xl mb-6" data-testid="footer-disclaimer">
            {SITE_DISCLAIMER}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} THE TRIP WORLD. {t("footer.rights")}
            </p>
            <div className="flex items-center gap-6">
              <a href="/privacy-policy" onClick={go("/privacy-policy")} data-testid="footer-privacy-link" className="text-xs text-slate-500 hover:text-gold transition-colors">
                {t("footer.privacy")}
              </a>
              <a href="/terms-conditions" onClick={go("/terms-conditions")} data-testid="footer-terms-link" className="text-xs text-slate-500 hover:text-gold transition-colors">
                {t("footer.terms")}
              </a>
              <a href="/disclaimer" onClick={go("/disclaimer")} data-testid="footer-disclaimer-link" className="text-xs text-slate-500 hover:text-gold transition-colors">
                {t("footer.disclaimerLink")}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="pointer-events-none select-none overflow-hidden">
        <p className="font-display font-black text-[16vw] leading-[0.8] text-white/[0.025] text-center whitespace-nowrap -mb-[4vw]">
          THE TRIP WORLD
        </p>
      </div>
    </footer>
  );
};

export default Footer;
